// Ctrl K / ⌘K search: the <dialog id="n-palette"> in Views/Shared/_Palette.cshtml. Pages come
// from NavigationCatalog, rendered into the page as JSON, so the list only holds what this user
// may open. Records (agents, flows, knowledge bases, connections) come from GET /Search?q=,
// which runs through the same tenant-filtered queries and permissions as the pages that list
// them. ARIA: a combobox driving a listbox.
import { $, escapeHtml, icon } from './dom';

interface PaletteItem {
	label: string;
	href: string;
	icon: string;
	meta?: string | null;
}

interface PaletteGroup {
	title: string;
	items: PaletteItem[];
}

interface PalettePage {
	label: string;
	href: string;
	icon: string;
	section: string;
}

export function initPalette(): void {
	const dialog = $<HTMLDialogElement>('#n-palette');
	if (!dialog || typeof dialog.showModal !== 'function') return;
	const input = $<HTMLInputElement>('#n-palette-input', dialog)!;
	const list = $<HTMLUListElement>('#n-palette-list', dialog)!;
	let pages: PalettePage[] = [];
	try {
		pages = JSON.parse($('#n-palette-pages')?.textContent || '[]') as PalettePage[];
	} catch {
		pages = [];
	}

	let options: HTMLLIElement[] = [];
	let active = -1;
	let remote: PaletteGroup[] = [];
	let requestId = 0;
	let timer: number | undefined;

	const matches = (text: string, query: string) => text.toLowerCase().includes(query);

	function render(keepHref?: string | null): void {
		const query = input.value.trim().toLowerCase();
		const groups: PaletteGroup[] = [];
		const pageHits = pages.filter((p) => !query || matches(p.label, query) || matches(p.section, query));
		if (pageHits.length) {
			groups.push({
				title: 'Pages',
				items: pageHits.map((p) => ({ label: p.label, href: p.href, icon: p.icon, meta: p.section })),
			});
		}
		for (const group of remote) if (group.items.length) groups.push(group);

		list.innerHTML = '';
		options = [];
		groups.forEach((group, gi) => {
			const heading = document.createElement('li');
			heading.className = 'n-palette-group';
			heading.setAttribute('role', 'presentation');
			heading.textContent = group.title;
			list.append(heading);
			group.items.forEach((item, ii) => {
				const li = document.createElement('li');
				li.id = `n-palette-opt-${gi}-${ii}`;
				li.className = 'n-palette-option';
				li.setAttribute('role', 'option');
				li.setAttribute('aria-selected', 'false');
				li.dataset.href = item.href;
				li.innerHTML = `${icon(item.icon, 'sm')}<span class="n-palette-label">${escapeHtml(item.label)}</span>${
					item.meta ? `<span class="n-palette-meta">${escapeHtml(item.meta)}</span>` : ''
				}`;
				li.addEventListener('mousedown', (e) => {
					e.preventDefault();
					go(item.href);
				});
				// Hover highlights without scrolling: scrolling under a moving pointer made the list jump.
				li.addEventListener('mousemove', () => {
					if (options[active] !== li) select(options.indexOf(li), false);
				});
				list.append(li);
				options.push(li);
			});
		});
		if (!options.length) {
			const empty = document.createElement('li');
			empty.className = 'n-palette-empty';
			empty.setAttribute('role', 'presentation');
			empty.textContent = query ? `Nothing matches "${input.value.trim()}".` : 'Start typing to search.';
			list.append(empty);
		}
		// Results that arrive while you arrow through the list keep your place.
		const keep = keepHref ? options.findIndex((o) => o.dataset.href === keepHref) : -1;
		select(keep >= 0 ? keep : options.length ? 0 : -1, false);
	}

	function select(index: number, scroll = true): void {
		if (active >= 0 && options[active]) options[active].setAttribute('aria-selected', 'false');
		active = index;
		if (active >= 0) {
			options[active].setAttribute('aria-selected', 'true');
			input.setAttribute('aria-activedescendant', options[active].id);
			if (scroll) options[active].scrollIntoView({ block: 'nearest' });
		} else {
			input.removeAttribute('aria-activedescendant');
		}
	}

	function go(href: string): void {
		dialog!.close();
		window.location.href = href;
	}

	async function search(): Promise<void> {
		const query = input.value.trim();
		const id = ++requestId;
		if (query.length < 2) {
			remote = [];
			render();
			return;
		}
		try {
			const response = await fetch(`/Search?q=${encodeURIComponent(query)}`, {
				headers: { Accept: 'application/json' },
				credentials: 'same-origin',
			});
			const data = response.ok ? ((await response.json()) as { groups?: PaletteGroup[] }) : { groups: [] };
			if (id !== requestId) return;
			remote = data.groups ?? [];
			render(active >= 0 && options[active] ? options[active].dataset.href : null);
		} catch {
			// Pages still work without the records.
		}
	}

	function open(): void {
		if (dialog!.open) return;
		input.value = '';
		remote = [];
		render();
		dialog!.showModal();
		input.focus();
	}

	input.addEventListener('input', () => {
		render();
		window.clearTimeout(timer);
		timer = window.setTimeout(() => void search(), 160);
	});
	input.addEventListener('keydown', (e) => {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (options.length) select((active + 1) % options.length);
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			if (options.length) select((active - 1 + options.length) % options.length);
		} else if (e.key === 'Enter') {
			e.preventDefault();
			if (active >= 0) go(options[active].dataset.href!);
		}
	});
	dialog.addEventListener('click', (e) => {
		if (e.target === dialog) dialog.close();
	});
	document.addEventListener('keydown', (e) => {
		if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
			e.preventDefault();
			if (dialog.open) dialog.close();
			else open();
		}
	});
	document.addEventListener('click', (e) => {
		if (e.target instanceof Element && e.target.closest('[data-n-palette-open]')) open();
	});
}
